import * as GQL from '~/graphql/generated/client';

export type PublishedPolicy = Omit<
  NonNullable<
    GQL.GetGuardianPublishedBundleQuery['guardianPublishedBundle']
  >['policies'][number],
  '__typename'
>;

export interface HistoryRow {
  publication: GQL.GuardianPublicationFieldsFragment;
  isActive: boolean;
}

export interface PolicyChange {
  before: PublishedPolicy;
  after: PublishedPolicy;
}

export interface PolicyDiff {
  added: PublishedPolicy[];
  removed: PublishedPolicy[];
  changed: PolicyChange[];
  unchangedCount: number;
}

// Only successful publishes are versions; the newest of them is what Fairly last sent, not a reading from the runtime.
export function historyRows(
  publications: readonly GQL.GuardianPublicationFieldsFragment[]
): HistoryRow[] {
  return publications
    .filter((p) => p.status === GQL.GuardianPublicationStatus.Published)
    .map((publication, index) => ({ publication, isActive: index === 0 }));
}

export function publisherLabel(
  requestedBy: GQL.GuardianPublicationFieldsFragment['requestedBy']
): string {
  if (!requestedBy) {
    return 'Unknown';
  }
  if (requestedBy.kind === 'job') {
    return 'Automatic publish';
  }
  return requestedBy.name ?? 'A user';
}

function comparable(policy: PublishedPolicy): string {
  return JSON.stringify({
    title: policy.title ?? null,
    severity: policy.severity ?? null,
    guidanceKind: policy.guidanceKind ?? null,
    guidanceSummary: policy.guidanceSummary ?? null,
    recommendedAction: policy.recommendedAction ?? null,
    intents: [...policy.intents].sort(),
    topics: [...policy.topics].sort(),
    actionClasses: [...policy.actionClasses].sort(),
  });
}

const FIELD_LABELS: [keyof PublishedPolicy, string][] = [
  ['title', 'title'],
  ['severity', 'severity'],
  ['guidanceKind', 'guidance'],
  ['guidanceSummary', 'summary'],
  ['recommendedAction', 'recommended action'],
  ['intents', 'intents'],
  ['topics', 'topics'],
  ['actionClasses', 'action classes'],
];

function fieldValue(policy: PublishedPolicy, key: keyof PublishedPolicy) {
  const value = policy[key];
  return Array.isArray(value) ? [...value].sort().join(',') : (value ?? null);
}

export function changedFields(change: PolicyChange): string[] {
  return FIELD_LABELS.filter(
    ([key]) => fieldValue(change.before, key) !== fieldValue(change.after, key)
  ).map(([, label]) => label);
}

// Reads as "what changed from the older version to the active one", matched by policy identifier.
export function diffPublishedPolicies(
  older: readonly PublishedPolicy[],
  active: readonly PublishedPolicy[]
): PolicyDiff {
  const olderById = new Map(older.map((p) => [p.identifier, p]));
  const activeById = new Map(active.map((p) => [p.identifier, p]));
  const diff: PolicyDiff = {
    added: [],
    removed: [],
    changed: [],
    unchangedCount: 0,
  };
  active.forEach((after) => {
    const before = olderById.get(after.identifier);
    if (!before) {
      diff.added.push(after);
    } else if (comparable(before) !== comparable(after)) {
      diff.changed.push({ before, after });
    } else {
      diff.unchangedCount += 1;
    }
  });
  older.forEach((before) => {
    if (!activeById.has(before.identifier)) {
      diff.removed.push(before);
    }
  });
  return diff;
}
